import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  collection, 
  query, 
  orderBy, 
  where, 
  doc, 
  updateDoc, 
  onSnapshot, 
  addDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaTimesCircle, FaEye, FaEyeSlash, FaFilter, FaSpinner, FaReply, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const AdminHelpScreen = () => {
  const [supportRequests, setSupportRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState('all'); // all, new, in-progress, resolved
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIds, setExpandedIds] = useState([]);
  const [isMobileListVisible, setIsMobileListVisible] = useState(false);

  // Fetch support requests
  useEffect(() => {
    let supportRequestsQuery;
    
    try {
    // Apply filter
    if (filter === 'all') {
      supportRequestsQuery = query(
        collection(db, 'supportRequests'),
        orderBy('createdAt', 'desc')
      );
    } else {
      supportRequestsQuery = query(
        collection(db, 'supportRequests'),
        where('status', '==', filter),
        orderBy('createdAt', 'desc')
      );
    }
    
    const unsubscribe = onSnapshot(
      supportRequestsQuery,
      (snapshot) => {
        const requests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
        setSupportRequests(requests);
        setLoading(false);
      },
      (error) => {
          // Check if the error is about missing index
          if (error.code === 'failed-precondition') {
            console.error('Need to create a composite index for this query:', error);
            // Fallback to client-side filtering
            const fallbackQuery = query(
              collection(db, 'supportRequests'),
              orderBy('createdAt', 'desc')
            );
            
            onSnapshot(
              fallbackQuery,
              (snapshot) => {
                const requests = snapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data(),
                  createdAt: doc.data().createdAt?.toDate() || new Date()
                }));
                // Filter on client side
                const filteredRequests = filter === 'all' 
                  ? requests 
                  : requests.filter(req => req.status === filter);
                setSupportRequests(filteredRequests);
                setLoading(false);
              },
              (fallbackError) => {
                console.error('Error in fallback query:', fallbackError);
                toast.error('Failed to load support requests');
                setLoading(false);
              }
            );
          } else {
            console.error('Error fetching support requests:', error);
            toast.error('Failed to load support requests');
            setLoading(false);
          }
        }
      );
      
      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up query:', error);
      setLoading(false);
    }
  }, [filter]);

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Unknown date';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Toggle request details visibility
  const toggleRequestDetails = (requestId) => {
    setExpandedIds(prev => 
      prev.includes(requestId)
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  // Handle request selection for response
  const selectRequest = (request) => {
    setSelectedRequest(request);
    setResponseText(request.resolveMessageFromAdmin || '');
  };

  // Update request status
  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      const requestRef = doc(db, 'supportRequests', requestId);
      await updateDoc(requestRef, {
        status: newStatus,
        previousStatus: selectedRequest.status, // Track the previous status
        updatedAt: serverTimestamp()
      });
      
      toast.success(`Request marked as ${newStatus}`);
      
      // If updating the currently selected request, update it in state
      if (selectedRequest && selectedRequest.id === requestId) {
        setSelectedRequest(prev => ({
          ...prev,
          status: newStatus,
          previousStatus: prev.status
        }));
      }
    } catch (error) {
      console.error('Error updating request status:', error);
      toast.error('Failed to update request status');
    }
  };

  // Submit response to request
  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    
    if (!selectedRequest) {
      toast.error('No request selected');
      return;
    }
    
    if (!responseText.trim()) {
      toast.error('Response cannot be empty');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Update the support request with admin's response
      await updateDoc(doc(db, 'supportRequests', selectedRequest.id), {
        resolveMessageFromAdmin: responseText,
        status: 'in-progress',
        updatedAt: serverTimestamp(),
        adminResponded: true,
        approvedByUser: null  // Use null instead of undefined
      });
      
      // Send notification to user
      await addDoc(collection(db, 'notifications'), {
        userId: selectedRequest.userId,
        message: 'You have received a response to your support request',
        createdAt: serverTimestamp(),
        read: false,
        type: 'support',
        supportRequestId: selectedRequest.id
      });
      
      toast.success('Response sent successfully');
      
      // Update selected request in state
      setSelectedRequest(prev => ({
        ...prev,
        resolveMessageFromAdmin: responseText,
        status: 'in-progress',
        adminResponded: true,
        approvedByUser: null
      }));
    } catch (error) {
      console.error('Error sending response:', error);
      toast.error('Failed to send response');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter requests based on search term
  const filteredRequests = supportRequests.filter(request => {
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (request.username && request.username.toLowerCase().includes(searchLower)) ||
      (request.userEmail && request.userEmail.toLowerCase().includes(searchLower)) ||
      (request.subject && request.subject.toLowerCase().includes(searchLower)) ||
      (request.message && request.message.toLowerCase().includes(searchLower))
    );
  });

  // Status badge component
  const StatusBadge = ({ status }) => {
    let bgColor, textColor, label;
    
    switch (status) {
      case 'new':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        label = 'New';
        break;
      case 'in-progress':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        label = 'In Progress';
        break;
      case 'resolved':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        label = 'Resolved';
        break;
      default:
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
        label = status || 'Unknown';
    }
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {label}
      </span>
    );
  };

  // Helper to check if request has been responded to
  const hasAdminResponse = (request) => {
    return request.adminResponded && request.resolveMessageFromAdmin;
  };

  // Helper to check approval status
  const getApprovalStatus = (request) => {
    if (request.approvedByUser === true) {
      return "Approved by user";
    } else if (request.approvedByUser === false) {
      return "Rejected by user";
    } else if (hasAdminResponse(request)) {
      return "Waiting for user approval";
    }
    return null;
  };

  return (
    <div className="p-4 sm:p-6 md:p-8">
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Support Requests Management
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Manage and respond to user support requests
        </p>
      </div>
      
      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex items-center gap-2">
          <FaFilter className="text-gray-500" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 p-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Requests</option>
            <option value="new">New</option>
            <option value="in-progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
        
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by user, email, subject or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 p-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>
      
      {/* Support requests list and details split view */}
      <div className="grid grid-cols-12 gap-6">
        {/* Requests List - 35% */}
        <div className="col-span-12 lg:col-span-4 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className="font-medium text-gray-900 dark:text-white">
                Support Requests ({filteredRequests.length})
              </h3>
              <button 
                onClick={() => setIsMobileListVisible(!isMobileListVisible)}
                className="lg:hidden text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {isMobileListVisible ? (
                  <FaChevronUp className="h-4 w-4" />
                ) : (
                  <FaChevronDown className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
          
          <div className={`overflow-y-auto max-h-[600px] ${!isMobileListVisible ? 'hidden lg:block' : ''}`}>
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <FaSpinner className="animate-spin text-primary-500 mr-2" />
                <span className="text-gray-600 dark:text-gray-400">Loading requests...</span>
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                No support requests found
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredRequests.map((request) => (
                  <li 
                    key={request.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 ${
                      selectedRequest?.id === request.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => selectRequest(request)}
                    >
                      <div className="flex justify-between mb-1">
                        <span className="font-medium text-gray-900 dark:text-white truncate">
                          {request.subject}
                        </span>
                        <StatusBadge status={request.status} />
                      </div>
                      
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        From: {request.username} ({request.userEmail})
                      </div>
                      
                      {hasAdminResponse(request) && (
                        <div className="text-xs text-gray-500 dark:text-gray-500 mb-1">
                          {getApprovalStatus(request) && (
                            <span className={`px-2 py-0.5 rounded-full ${
                              request.approvedByUser === true 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                : request.approvedByUser === false
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            }`}>
                              {getApprovalStatus(request)}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="text-sm text-gray-500 dark:text-gray-500 flex justify-between">
                        <span>{formatDate(request.createdAt)}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRequestDetails(request.id);
                          }}
                          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          {expandedIds.includes(request.id) ? (
                            <FaEyeSlash className="h-4 w-4" />
                          ) : (
                            <FaEye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      
                      {expandedIds.includes(request.id) && (
                        <div className="mt-3 pt-3 border-t dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300">
                          <p className="whitespace-pre-wrap break-words">
                            {request.message}
                          </p>
                          
                          {hasAdminResponse(request) && (
                            <div className="mt-3 pt-3 border-t dark:border-gray-600">
                              <p className="font-medium mb-1">Admin Response:</p>
                              <p className="whitespace-pre-wrap break-words text-xs bg-gray-50 dark:bg-gray-700 p-2 rounded">
                                {request.resolveMessageFromAdmin}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        
        {/* Response Panel - 65% */}
        <div className="col-span-12 lg:col-span-8 bg-white dark:bg-gray-800 rounded-lg shadow">
          {selectedRequest ? (
            <div className="h-full flex flex-col">
              {/* Request Header */}
              <div className="p-4 border-b dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    {selectedRequest.subject}
                  </h3>
                  <StatusBadge status={selectedRequest.status} />
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  From: {selectedRequest.username} ({selectedRequest.userEmail})
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  Submitted: {formatDate(selectedRequest.createdAt)}
                </div>
                
                {hasAdminResponse(selectedRequest) && getApprovalStatus(selectedRequest) && (
                  <div className="mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      selectedRequest.approvedByUser === true 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : selectedRequest.approvedByUser === false
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {getApprovalStatus(selectedRequest)}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Request Body */}
              <div className="p-4 border-b dark:border-gray-700 overflow-y-auto flex-1">
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Message:
                  </h4>
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded whitespace-pre-wrap break-words text-gray-700 dark:text-gray-300">
                    {selectedRequest.message}
                  </div>
                </div>
                
                {/* Show previous admin response if exists */}
                {hasAdminResponse(selectedRequest) && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Your Previous Response:
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded whitespace-pre-wrap break-words text-gray-700 dark:text-gray-300 border-l-4 border-primary-500">
                      {selectedRequest.resolveMessageFromAdmin}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                <div className="flex flex-wrap gap-2">
                  {selectedRequest.status !== 'in-progress' && selectedRequest.status !== 'resolved' && (
                    <button
                      onClick={() => updateRequestStatus(selectedRequest.id, 'in-progress')}
                      className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-md text-sm"
                    >
                      Mark In Progress
                    </button>
                  )}
                  
                  {selectedRequest.status !== 'resolved' && (
                    <button
                      onClick={() => updateRequestStatus(selectedRequest.id, 'resolved')}
                      className="px-3 py-1 bg-green-100 text-green-800 rounded-md text-sm"
                    >
                      <FaCheckCircle className="inline mr-1" />
                      Mark Resolved
                    </button>
                  )}
                  
                  {selectedRequest.status === 'resolved' && (
                    <button
                      onClick={() => updateRequestStatus(selectedRequest.id, 'in-progress')}
                      className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-md text-sm"
                    >
                      <FaTimesCircle className="inline mr-1" />
                      Reopen
                    </button>
                  )}
                </div>
              </div>
              
              {/* Response Form */}
              <div className="p-4">
                <form onSubmit={handleSubmitResponse}>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    {hasAdminResponse(selectedRequest) ? 'Update Response:' : 'Send Response:'}
                  </h4>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Type your response here..."
                    rows={4}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-3"
                    disabled={selectedRequest.status === 'resolved' || isSubmitting}
                  />
                  
                  <button
                    type="submit"
                    disabled={selectedRequest.status === 'resolved' || isSubmitting || !responseText.trim()}
                    className={`flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                      (selectedRequest.status === 'resolved' || isSubmitting || !responseText.trim()) ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaReply className="mr-2" />
                        {hasAdminResponse(selectedRequest) ? 'Update Response' : 'Send Response'}
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full p-8 text-center">
              <div className="text-gray-500 dark:text-gray-400">
                <p className="mb-2">Select a support request to view details and respond</p>
                <p className="text-sm">You can mark requests as in-progress or resolved</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminHelpScreen;
